import { USER_TYPE } from "../constants";

/**
 * Rename a cash account based on the currentUser type and the account owner.
 * If the account type is not CASH, the account is returned as is.
 * If the currentUser is a USER, the account name is updated to include the currentUser's name
 * if the account is owned by the currentUser, or "CASH (Company)" if the account is owned by the company admin.
 * If the currentUser is an ADMIN, the account name is updated to include the admin's name.
 * @param {Object} account The account object to be renamed.
 * @param {Object} currentUser The currentUser object.
 * @param {number} companyAdminId The company admin's currentUser ID.
 * @returns {Object} The renamed account object.
 */
export const renameCashAccount = (account, currentUser, companyAdminId) => {
  if (!["CASH", "USER"].includes(account.type)) return account;

  if (account.ownerUserId === currentUser.id) {
    return { ...account, name: `CASH (${currentUser?.firstName} ${currentUser?.lastName})` };
  } else if (currentUser.type === USER_TYPE.USER && account.ownerUserId === companyAdminId) {
    return { ...account, name: "CASH (Company)" };
  } else if (account.ownerUserId !== currentUser.id && account?.type === "CASH") {
    return { ...account, name: `CASH (${account?.ownerUser?.firstName} ${account?.ownerUser?.lastName})` };
  }

  return account;
};

/**
 * Returns an array of accounts ordered by user and admin accounts.
 * @param {{ rows: Array<Object>, user: Object, companyAdminId: number }} params
 * @returns {Array<Object>} An array of accounts ordered by user and admin accounts.
 *
 * The function first filters the accounts to get the user accounts and admin accounts.
 * Then it maps over the filtered accounts and renames the cash accounts using the renameCashAccount function.
 * Finally, it returns an array of the user accounts followed by the admin accounts.
 */
export const getUserOrderedAccounts = (rows = [], user, companyAdminId) => {
  const userAccounts = rows
    .filter((account) => account.ownerUserId === user.id)
    .map((account) => renameCashAccount(account, user, companyAdminId));

  const adminAccounts = rows
    .filter((account) => account.ownerUserId === companyAdminId)
    .map((account) => renameCashAccount(account, user, companyAdminId));

  return [...userAccounts, ...adminAccounts];
};

/**
 * Formats the accounts response to include renamed cash accounts
 * based on the user and company admin ID.
 *
 * If the user is a USER, it reorders the accounts to put the user
 * accounts first, followed by the admin accounts.
 *
 * If the user is an ADMIN, it preserves the original order and
 * renames only the cash accounts.
 *
 * @param {Object} accountsResponse - The response from the accounts API.
 * @param {{ user: Object, companyAdminId: number }} - The user object and
 * the company admin ID.
 * @returns {Object} - The formatted accounts response.
 */
export const formatAdminUserAccounts = (accountsResponse, { user, companyAdminId }) => {
  const { data } = accountsResponse;
  const rows = data?.rows || [];

  // USER: reorder accounts to have user accounts first then admin accounts and rename cash accounts
  if (user.type === USER_TYPE.USER) {
    return {
      data: {
        rows: getUserOrderedAccounts(rows, user, companyAdminId),
        count: data?.count,
      },
    };
  }

  // ADMIN: rename cash accounts only
  return {
    ...accountsResponse,
    data: {
      ...data,
      rows: rows.map((account) => renameCashAccount(account, user, companyAdminId)),
    },
  };
};

/** Label of the company owner's cash account in company-level views (reports, PDFs, report filters). */
export const COMPANY_CASH_ACCOUNT_NAME = "CASH (Company)";

export interface NamedAccount {
  name: string;
  type?: string;
  ownerUserId?: number | null;
  ownerUser?: { firstName?: string | null; lastName?: string | null } | null;
}

/**
 * Viewer-independent account name for company-level views such as reports and their filters.
 * Same convention as `renameCashAccount`, without depending on who is looking:
 * - the company owner's CASH account → `CASH (Company)`
 * - any other CASH account → `CASH (<First> <Last>)`, or `CASH (<stored name>)` if the owner is not loaded
 * - any other account → its stored name
 * Must match the backend's `formatCashAccountName` (cowallet-backend `src/analytics/helper.ts`).
 */
export const getCompanyCashAccountName = (account: NamedAccount, companyOwnerUserId?: number | null): string => {
  if (account.type !== "CASH") return account.name;
  if (companyOwnerUserId && account.ownerUserId === companyOwnerUserId) return COMPANY_CASH_ACCOUNT_NAME;

  const ownerName = `${account.ownerUser?.firstName ?? ""} ${account.ownerUser?.lastName ?? ""}`.trim();
  return `CASH (${ownerName || account.name})`;
};
